import { Link } from "react-router-dom";
import { Sparkles, Timer, Share2, Bell, Keyboard, Check, Smartphone } from "lucide-react";
import SEOHead, {
  organizationSchema,
  websiteSchema,
  softwareApplicationSchema,
  faqSchema,
} from "@/components/SEOHead";
import MarketingHeader from "@/components/MarketingHeader";
import MarketingFooter from "@/components/MarketingFooter";
import FaqBlock, { CtaBlock } from "@/components/FaqBlock";
import { InstallPWAButton, InstallPWAFloatingHint } from "@/components/InstallPWA";
import { ALL_FAQS, COMPETITORS, USE_CASES, SITE } from "@/lib/seoData";

const FEATURES = [
  { Icon: Sparkles, h: "AI breaks any task down", p: "One click and Claude returns 4–6 specific subtasks. Haiku for short tasks, Sonnet for complex ones — kept cheap on purpose." },
  { Icon: Timer, h: "Focus timer with audio cues", p: "Pomodoro-style timer with 30-second and 10-second warnings + completion chime." },
  { Icon: Smartphone, h: "Install to your home screen", p: "Add BreakItDown to iPhone / Android / desktop. One tap to open, stays signed in for weeks." },
  { Icon: Share2, h: "Read-only share links", p: "Public link for any task — no sign-in needed for the reader." },
  { Icon: Bell, h: "Daily AI briefing", p: "Top-3 tasks pushed as a browser notification at your chosen time." },
  { Icon: Keyboard, h: "Keyboard shortcuts", p: "/ focus add, B break down, Space toggle timer, Esc exit focus." },
];

export default function HomePage() {
  return (
    <div className="min-h-screen bg-white flex flex-col">
      <SEOHead
        title="BreakItDown — Free AI Task Management. Break Big Tasks Down."
        description={SITE.description}
        path="/"
        schemas={[
          organizationSchema(),
          websiteSchema(),
          softwareApplicationSchema(),
          faqSchema(ALL_FAQS.homepage),
        ]}
      />
      <MarketingHeader />

      <main className="flex-1">
        {/* Hero */}
        <section className="max-w-6xl mx-auto px-6 md:px-10 py-16 md:py-24 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-zinc-500 mb-4">
              Free · Powered by Claude Sonnet 4.5
            </p>
            <h1 className="text-5xl sm:text-6xl font-semibold tracking-tight leading-[0.95] text-zinc-900">
              Break big tasks
              <br /> down. Get them done.
            </h1>
            <p className="text-base md:text-lg text-zinc-600 mt-6 max-w-lg leading-relaxed">
              BreakItDown is an AI-powered task manager that turns any vague task into a clear
              checklist. Built for focused individuals, freelancers, and small teams.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link
                to="/login"
                className="inline-flex items-center gap-2 px-6 py-3 bg-zinc-900 hover:bg-[#002FA7] text-white text-sm font-medium uppercase tracking-[0.12em] transition-colors"
                data-testid="hero-cta"
              >
                Start free →
              </Link>
              <InstallPWAButton
                className="inline-flex items-center gap-2 px-6 py-3 border border-zinc-300 hover:border-zinc-900 text-zinc-900 text-sm font-medium uppercase tracking-[0.12em] transition-colors"
                dataTestId="hero-install-pwa"
              >
                Add to home screen
              </InstallPWAButton>
              <Link
                to="/pricing"
                className="inline-flex items-center px-6 py-3 border border-zinc-300 text-zinc-900 text-sm font-medium uppercase tracking-[0.12em] hover:bg-zinc-50"
              >
                See what's free
              </Link>
            </div>
            <ul className="flex flex-wrap gap-x-5 gap-y-2 mt-6 text-xs text-zinc-500">
              <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-emerald-600" strokeWidth={3} /> No credit card</li>
              <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-emerald-600" strokeWidth={3} /> No paid tier</li>
              <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-emerald-600" strokeWidth={3} /> AI included</li>
            </ul>
          </div>
          <div className="relative">
            <div className="border border-zinc-200 bg-white shadow-[0_30px_80px_-30px_rgba(0,0,0,0.15)]">
              <div className="px-5 py-4 border-b border-zinc-200 flex items-center gap-2">
                <span className="w-2 h-2 bg-red-400"></span>
                <span className="w-2 h-2 bg-amber-400"></span>
                <span className="w-2 h-2 bg-emerald-400"></span>
              </div>
              <div className="p-6">
                <p className="text-xs font-semibold uppercase tracking-[0.15em] text-zinc-500 mb-1">Today's focus</p>
                <h3 className="text-2xl font-semibold text-zinc-900 mb-4">Plan team offsite</h3>
                <div className="text-xs text-zinc-500 mb-3">AI generated 6 subtasks</div>
                <ul className="space-y-2 text-sm">
                  {[
                    "Research beach accommodation in Sydney for 8 people",
                    "Find half-day group hiking trails nearby",
                    "Book within $5000 budget",
                    "Reserve Thursday dinner restaurant",
                    "Create itinerary with timings",
                    "Send calendar invites to team",
                  ].map((s, i) => (
                    <li key={i} className="flex items-start gap-2.5">
                      <span className="w-4 h-4 border border-zinc-400 mt-0.5 shrink-0" />
                      <span className="text-zinc-700">{s}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* AI summary block — optimised for AI extraction */}
        <section className="border-y border-zinc-200 bg-zinc-50">
          <div className="max-w-6xl mx-auto px-6 md:px-10 py-10">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-zinc-500 mb-3">Quick answer</p>
            <p className="text-lg text-zinc-800 leading-relaxed max-w-4xl">
              <strong>BreakItDown</strong> is a free, AI-powered task management app that automatically
              splits big tasks into smaller subtasks using <strong>Claude Sonnet 4.5</strong>. It
              includes a Pomodoro-style focus timer with audio cues, one-tap{" "}
              <strong>install to your home screen</strong> (works like a native app on iPhone,
              Android, and desktop), public read-only share links per task, and a daily AI briefing
              pushed to your browser. It runs in any modern browser, works on desktop and mobile,
              and is well-suited to freelancers, small remote teams, and anyone juggling
              a long list of fuzzy projects.
            </p>
          </div>
        </section>

        {/* Features */}
        <section className="max-w-6xl mx-auto px-6 md:px-10 py-16 md:py-24">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-zinc-500 mb-3">Features</p>
          <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-zinc-900 mb-12 max-w-2xl">
            Built around one idea: <span className="text-zinc-400">a fuzzy task → a clear checklist → done.</span>
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-zinc-200 border border-zinc-200">
            {FEATURES.map(({ Icon, h, p }) => (
              <div key={h} className="bg-white p-6">
                <Icon className="w-6 h-6 text-[#002FA7] mb-4" strokeWidth={1.5} />
                <h3 className="text-base font-semibold text-zinc-900 mb-2">{h}</h3>
                <p className="text-sm text-zinc-600 leading-relaxed">{p}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Compare grid */}
        <section className="max-w-6xl mx-auto px-6 md:px-10 py-16 border-t border-zinc-200">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-zinc-500 mb-3">Compare</p>
          <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-zinc-900 mb-8">
            How BreakItDown stacks up
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
            {COMPETITORS.map((c) => (
              <Link
                key={c.slug}
                to={`/compare/${c.slug}`}
                className="border border-zinc-200 p-5 hover:border-zinc-900 transition-colors group"
              >
                <div className="text-xs uppercase tracking-[0.12em] text-zinc-500 mb-1">BreakItDown vs</div>
                <div className="text-lg font-semibold text-zinc-900 group-hover:text-[#002FA7]">{c.name}</div>
              </Link>
            ))}
          </div>
        </section>

        {/* Use case grid */}
        <section className="max-w-6xl mx-auto px-6 md:px-10 py-16 border-t border-zinc-200">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-zinc-500 mb-3">Use cases</p>
          <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-zinc-900 mb-8">Who BreakItDown is for</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-zinc-200 border border-zinc-200">
            {USE_CASES.filter((u) => u.slug !== "adhd-productivity").slice(0, 9).map((u) => (
              <Link key={u.slug} to={`/use-cases/${u.slug}`} className="bg-white p-5 hover:bg-zinc-50 transition-colors">
                <div className="text-base font-semibold text-zinc-900">{u.h1.split(":")[0].split(" — ")[0]}</div>
                <div className="text-sm text-zinc-500 mt-1 line-clamp-2">{u.description}</div>
              </Link>
            ))}
          </div>
        </section>

        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <FaqBlock faqs={ALL_FAQS.homepage} />
          <CtaBlock />
        </div>
      </main>

      <MarketingFooter />
      <InstallPWAFloatingHint />
    </div>
  );
}
